<template>
  <!--
    Off-screen host that mounts the analytics tabs that DON'T live on the
    homepage so a single "Download all" PDF can capture their charts/KPIs.
    Positioned off-screen (not display:none) at a real desktop width so Chart.js
    canvases render at full resolution. Each metrics component fetches its own
    data on mount and reads the shared division from useDivisionSelection,
    mirroring a normal tab visit.

    The four homepage-native sections (Summary snapshot, Adoption, App
    engagement, App features) are NOT re-mounted here — the homepage already has
    them loaded and rendered, so it threads those live sections in via the
    `homepageSections` / `pageExtras` props, avoiding four redundant re-fetches
    per download.
  -->
  <div ref="rootRef" class="all-analytics-capture" aria-hidden="true">
    <build-metrics v-if="isBuildEnabled" ref="buildRef" />
    <coach-metrics v-if="isCoachEnabled" ref="coachRef" />
    <track-metrics ref="trackRef" />
    <budget-metrics v-if="isBudgetEnabled" ref="budgetRef" />
    <rewards-metrics v-if="isRewardsEnabled" ref="rewardsRef" />
    <stream-metrics ref="streamRef" />
    <sdl-metrics v-if="isSdlEnabled" ref="sdlRef" />
    <pension-metrics v-if="isPensionFindCombineEnabled" ref="pensionRef" />

    <dashboard-impact ref="dashboardImpactRef" />
    <workforce-impact-section
      ref="workforceImpactRef"
      :metrics="sectorMetrics"
      :sector="sector"
      :loading="sectorLoading"
    />
  </div>
</template>

<script setup>
import { onBeforeMount, ref } from 'vue';
import ApiClient from '@/ApiClient';
import useProductAvailability from '@/composables/useProductAvailability';
import BudgetMetrics from '@/pages/analyticsApp/components/BudgetMetrics.vue';
import BuildMetrics, { buildMetricsSections } from '@/pages/analyticsApp/components/BuildMetrics.vue';
import CoachMetrics, { coachMetricsSections } from '@/pages/analyticsApp/components/CoachMetrics.vue';
import RewardsMetrics, { rewardsMetricsSections } from '@/pages/analyticsApp/components/RewardsMetrics.vue';
import SdlMetrics, { sdlMetricsSections } from '@/pages/analyticsApp/components/SdlMetrics.vue';
import StreamMetrics, { streamMetricsSections } from '@/pages/analyticsApp/components/StreamMetrics.vue';
import TrackMetrics from '@/pages/analyticsApp/components/TrackMetrics.vue';
import DashboardImpact from '@/pages/analyticsBenchmark/components/DashboardImpact.vue';
import WorkforceImpactSection from '@/pages/analyticsBenchmark/components/WorkforceImpactSection.vue';
import PensionMetrics, { pensionMetricsSections } from '@/pages/analyticsFindCombine/PensionMetrics.vue';
import { wholeSection } from '@/utils/analyticsSections';

const props = defineProps({
  // Descriptors for the homepage's already-rendered sections (Adoption, App
  // engagement, App features), prepended to the capture's own off-screen tabs
  // so the "Download all" PDF keeps its Summary-first section order without
  // re-mounting the homepage-native components.
  homepageSections: { type: Function, default: null },
  // Summary snapshot KPIs for the opening PDF sheet, read from the homepage's
  // live SnapshotMetrics instance.
  pageExtras: { type: Function, default: null },
  // Predicate that is true while any homepage-native section is (re)fetching.
  // Those sections aren't mounted here, so ensureReady can't see their spinners;
  // it polls this instead to avoid capturing before a refetch resolves.
  homepageBusy: { type: Function, default: null },
});

const { isBuildEnabled, isCoachEnabled, isBudgetEnabled, isRewardsEnabled, isSdlEnabled, isPensionFindCombineEnabled } =
  useProductAvailability();

const rootRef = ref(null);

const buildRef = ref(null);
const coachRef = ref(null);
const trackRef = ref(null);
const budgetRef = ref(null);
const rewardsRef = ref(null);
const streamRef = ref(null);
const sdlRef = ref(null);
const pensionRef = ref(null);
const dashboardImpactRef = ref(null);
const workforceImpactRef = ref(null);

// Impact's Workforce section is prop-driven; the page normally fetches these.
const sectorLoading = ref(true);
const sector = ref(null);
const sectorMetrics = ref([]);

onBeforeMount(async () => {
  try {
    const data = await ApiClient.getSectorBenchmarks();
    sector.value = data?.sector || null;
    sectorMetrics.value = data?.metrics || [];
  } catch (error) {
    console.error('[Download all] Failed to fetch sector benchmarks:', error);
  }
  sectorLoading.value = false;
});

// Ordered to mirror the analytics tab order. The homepage's own sections
// (Adoption / App engagement / App features) come first so the Summary snapshot
// KPIs (page extras) land on the opening PDF sheet. Descriptors for disabled
// products resolve `hasData() === false` (their ref is null) and are filtered
// out by collectSections. Multi-subsection tabs (Savings / Education / Rewards /
// Pay / Loans) reuse the same section factory as their per-tab page, so there
// is one source of truth per component.
function allSections() {
  return [
    ...(props.homepageSections?.() ?? []),

    ...buildMetricsSections(buildRef),
    ...coachMetricsSections(coachRef),

    wholeSection(trackRef, 'Track'),
    wholeSection(budgetRef, 'Budget'),

    ...rewardsMetricsSections(rewardsRef),
    ...streamMetricsSections(streamRef),
    ...sdlMetricsSections(sdlRef),
    ...pensionMetricsSections(pensionRef),

    wholeSection(dashboardImpactRef, 'Stream Impact'),
    wholeSection(workforceImpactRef, 'Workforce Impact', {
      getPdfData: () => workforceImpactRef.value?.getPdfData?.() ?? {},
    }),
  ];
}

function collectSections() {
  return allSections()
    .filter(section => section.hasData())
    .map(section => ({
      label: section.label,
      inlineKpis: section.inlineKpis,
      getContainerEl: section.getContainerEl,
      ...(section.getPdfData ? { getPdfData: section.getPdfData } : {}),
    }));
}

// Summary snapshot KPIs, applied to the first PDF sheet only. Sourced from the
// homepage's live SnapshotMetrics instance rather than a re-mounted copy.
function getPageExtras() {
  return props.pageExtras?.() ?? null;
}

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
const nextFrame = () => new Promise(resolve => requestAnimationFrame(resolve));

/**
 * Resolve once every tab feeding the capture has finished loading. Off-screen
 * tabs hosted here render an `.app-loading` spinner while fetching, so "no
 * spinners left" (within this host) is our done-loading signal for them. The
 * homepage-native sections aren't mounted here, so their spinners are invisible
 * to us — we poll `homepageBusy()` instead so a download fired mid-refetch (e.g.
 * right after a division switch) waits for fresh data rather than silently
 * dropping a not-yet-loaded section. We require the settled state to hold for a
 * couple of consecutive polls (staggered API responses) and cap the total wait.
 */
async function ensureReady({
  timeout = 25000,
  interval = 250,
  settlePolls = 3,
  minWait = 1500,
  animationSettle = 1200,
} = {}) {
  const startedAt = performance.now();
  await sleep(minWait);
  let stable = 0;
  while (performance.now() - startedAt < timeout) {
    const hostLoading = (rootRef.value?.querySelectorAll('.app-loading').length || 0) > 0;
    const stillLoading = hostLoading || sectorLoading.value || !!props.homepageBusy?.();
    stable = stillLoading ? 0 : stable + 1;
    if (stable >= settlePolls) {
      break;
    }
    await sleep(interval);
  }
  // Charts mount once their data resolves and animate (~1s) into place; wait
  // that out so the captured canvases show the final, fully-drawn state.
  await sleep(animationSettle);
  await nextFrame();
  await nextFrame();
}

defineExpose({ collectSections, getPageExtras, ensureReady });
</script>

<style lang="scss" scoped>
.all-analytics-capture {
  position: fixed;
  top: 0;
  left: -10000px;
  width: 1200px;
  z-index: -1;
  pointer-events: none;
}
</style>
